import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import Layout from "../components/Layout";
import AssetNav from "../components/AssetNav";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import SetupGuide from "../components/SetupGuide";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function AssetSettings() {
  const [mapping, setMapping] = useState({ rooms: [], room_types: [], unmapped: [] });
  const [templates, setTemplates] = useState([]);
  const [items, setItems] = useState([]);
  const [depts, setDepts] = useState([]);
  const [cats, setCats] = useState([]);
  const [checklist, setChecklist] = useState(null);
  const [settings, setSettings] = useState({});
  const [newDept, setNewDept] = useState("");
  const [newCat, setNewCat] = useState("");
  const [newUom, setNewUom] = useState({ name: "", decimals: 0 });
  const [editingTemplateId, setEditingTemplateId] = useState(null);
  const [newTemplateItem, setNewTemplateItem] = useState({});

  const load = async () => {
    const [m, t, i, d, c, s, ck] = await Promise.all([
      api.get("/assets/room-mapping"), api.get("/assets/templates"), api.get("/assets/items"),
      api.get("/assets/departments"), api.get("/assets/categories"), api.get("/assets/settings"),
      api.get("/assets/setup-checklist"),
    ]);
    setMapping(m.data); setTemplates(t.data); setItems(i.data); setDepts(d.data); setCats(c.data);
    setSettings(s.data); setChecklist(ck.data);
  };
  useEffect(() => { load(); }, []);

  const itemName = id => items.find(i => i.id === id)?.name || id;

  const mapRoom = async (room_id, asset_room_type_id) => {
    try { await api.post("/assets/room-mapping", { room_id, asset_room_type_id }); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const setLineQty = (tpl, idx, qty) => {
    setTemplates(prev => prev.map(t => t.id !== tpl.id ? t : {
      ...t, lines: t.lines.map((l, i) => i === idx ? { ...l, qty: qty === "" ? null : Number(qty) } : l),
    }));
  };
  const setLineMandatory = (tpl, idx, mandatory) => {
    setTemplates(prev => prev.map(t => t.id !== tpl.id ? t : {
      ...t, lines: t.lines.map((l, i) => i === idx ? { ...l, mandatory } : l),
    }));
  };

  const saveTemplate = async (tpl) => {
    try {
      await api.patch(`/assets/templates/${tpl.id}`, { lines: tpl.lines });
      setEditingTemplateId(null);
      toast.success("Template saved");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const updateTemplateLines = (tpl, lines) => {
    setTemplates(prev => prev.map(t => t.id !== tpl.id ? t : { ...t, lines }));
  };

  const addTemplateItem = (tpl) => {
    const itemId = newTemplateItem[tpl.id];
    if (!itemId || tpl.lines.some(line => line.item_id === itemId)) {
      toast.error("Choose an item that is not already in this template");
      return;
    }
    updateTemplateLines(tpl, [...tpl.lines, { item_id: itemId, qty: 1, mandatory: true }]);
    setNewTemplateItem(prev => ({ ...prev, [tpl.id]: "" }));
  };

  const deleteTemplate = async (tpl) => {
    if (!window.confirm(`Delete amenity template “${tpl.name}”? This cannot be undone.`)) {
      return;
    }
    try {
      await api.delete(`/assets/templates/${tpl.id}`);
      toast.success("Amenity template deleted");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const activateTemplate = async (tpl) => {
    try {
      await api.patch(`/assets/templates/${tpl.id}`, { lines: tpl.lines });
      await api.post(`/assets/templates/${tpl.id}/activate`);
      toast.success("Template activated"); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const addNamed = async (path, name, reset) => {
    if (!name) return;
    try { await api.post(`/assets/${path}`, { name }); reset(""); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const toggleNamed = async (path, id, active) => {
    try { await api.patch(`/assets/${path}/${id}`, { active }); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const saveCustomUoms = async (list) => {
    try {
      const { data } = await api.patch("/assets/settings", { custom_uoms: list });
      setSettings(data); toast.success("Units of measure saved");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const addUom = () => {
    const name = (newUom.name || "").trim();
    if (!name) return toast.error("Enter a UOM name");
    const existing = settings.custom_uoms || [];
    if (existing.some(u => (u.name || "").toLowerCase() === name.toLowerCase())) return toast.error("This UOM already exists");
    saveCustomUoms([...existing, { name, decimals: Number(newUom.decimals) }]);
    setNewUom({ name: "", decimals: 0 });
  };
  const removeUom = (name) => saveCustomUoms((settings.custom_uoms || []).filter(u => u.name !== name));

  return (
    <Layout>
      <div className="mb-4"><h1 className="section-title">Asset Settings</h1></div>
      <AssetNav />

      {/* Setup Completion Checklist card intentionally hidden — Preview requirement.
          The underlying setup validations (opening finalized, room mapping,
          template activation) remain enforced server-side. */}
      {false && checklist && (
        <Card className="p-4 mb-6" data-testid="setup-checklist-card">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
            <div className="section-sub font-medium text-slate-700">Setup Completion Checklist</div>
            {checklist.operational
              ? <Badge className="bg-emerald-100 border text-emerald-800" data-testid="module-operational">Module Operational</Badge>
              : <Badge className="bg-amber-100 border text-amber-800" data-testid="module-setup-pending">Setup Pending — complete the 3 mandatory steps</Badge>}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
            <Badge className={checklist.room_mapping_complete ? "bg-emerald-100 border text-emerald-800" : "bg-amber-100 border text-amber-800"}>
              1. Room mapping {checklist.room_mapping_complete ? "complete" : `— ${checklist.rooms_unmapped} pending`}</Badge>
            <Badge className={checklist.templates_activated > 0 ? "bg-emerald-100 border text-emerald-800" : "bg-amber-100 border text-amber-800"}>
              2. Templates {checklist.templates_activated}/{checklist.templates_total} active</Badge>
            <Badge className={checklist.opening_finalized ? "bg-emerald-100 border text-emerald-800" : "bg-amber-100 border text-amber-800"}>
              3. Opening {checklist.opening_finalized ? "finalized" : "pending"}</Badge>
            <Badge className="bg-slate-100 border text-slate-600">
              Optional — Vendors: {checklist.vendors}</Badge>
          </div>
        </Card>
      )}

      <Tabs defaultValue="mapping">
        <TabsList className="mb-4 flex-wrap h-auto">
          <TabsTrigger value="mapping" data-testid="tab-room-mapping">Room Type Mapping</TabsTrigger>
          <TabsTrigger value="templates" data-testid="tab-templates">Amenity Templates</TabsTrigger>
          <TabsTrigger value="config" data-testid="tab-config">Departments & Categories</TabsTrigger>
          <TabsTrigger value="prefs" data-testid="tab-prefs">Preferences</TabsTrigger>
        </TabsList>

        <TabsContent value="mapping">
          <SetupGuide current="mapping" />
          <Card className="p-0 overflow-x-auto">
            <table className="vr-table" data-testid="room-mapping-table">
              <thead><tr><th>Room</th><th>Status</th><th>Asset Room Type</th></tr></thead>
              <tbody>
                {mapping.rooms.map(r => (
                  <tr key={r.id}>
                    <td className="font-medium">Room {r.number}</td>
                    <td className="text-xs">{r.status}</td>
                    <td>
                      <Select value={r.asset_room_type_id || ""} onValueChange={v => mapRoom(r.id, v)}>
                        <SelectTrigger className="h-9 w-44" data-testid={`map-room-${r.number}`}><SelectValue placeholder="Unassigned" /></SelectTrigger>
                        <SelectContent>{mapping.room_types.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="templates">
          <SetupGuide current="templates" />
          <Card className="p-4 mb-3 border-amber-300 bg-amber-50 text-sm text-amber-900">
            Draft templates are excluded from stock calculations. Enter a confirmed quantity for every line, then activate. Activated templates power the room checklist and shortage calculation.
          </Card>
          <div className="space-y-4">
            {templates.map(tpl => (
              <Card className="p-4" key={tpl.id} data-testid={`template-${tpl.id}`}>
                <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                  <div>
                    <span className="font-medium">{tpl.name}</span>
                    <Badge className={`ml-2 ${tpl.status === "active" ? "bg-emerald-100 border text-emerald-800" : "bg-amber-100 border text-amber-800"}`}>{tpl.status === "active" ? "Active" : "Draft"}</Badge>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setEditingTemplateId(tpl.id)}
                      data-testid={`edit-template-${tpl.id}`}
                    >
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => saveTemplate(tpl)}
                      disabled={editingTemplateId !== tpl.id}
                      data-testid={`save-template-${tpl.id}`}
                    >
                      Save
                    </Button>
                    {tpl.status !== "active" && (
                      <Button
                        size="sm"
                        className="bg-slate-900 text-white hover:bg-slate-800"
                        onClick={() => activateTemplate(tpl)}
                        data-testid={`activate-template-${tpl.id}`}
                      >
                        Activate
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-red-200 text-red-700 hover:bg-red-50"
                      onClick={() => deleteTemplate(tpl)}
                      data-testid={`delete-template-${tpl.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
                    </Button>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="vr-table">
                    <thead><tr><th>Item</th><th className="num">Qty per room</th><th>Mandatory</th><th>Actions</th></tr></thead>
                    <tbody>
                      {tpl.lines.map((l, idx) => (
                        <tr key={l.item_id || idx}>
                          <td>{itemName(l.item_id)}</td>
                          <td className="num"><Input type="number" min="0" className="h-9 w-24 ml-auto text-right" value={l.qty ?? ""} placeholder="—"
                            disabled={editingTemplateId !== tpl.id}
                            onChange={e => setLineQty(tpl, idx, e.target.value)} data-testid={`tpl-${tpl.id}-line-${idx}-qty`} /></td>
                          <td><Switch checked={!!l.mandatory} disabled={editingTemplateId !== tpl.id} onCheckedChange={v => setLineMandatory(tpl, idx, v)} /> <span className="text-xs text-slate-500 ml-1">{l.mandatory ? "Mandatory" : "Optional"}</span></td>
                          <td>{editingTemplateId === tpl.id && <Button size="sm" variant="ghost" className="text-red-700" onClick={() => updateTemplateLines(tpl, tpl.lines.filter((_, i) => i !== idx))} data-testid={`remove-template-line-${tpl.id}-${idx}`}>Remove</Button>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {editingTemplateId === tpl.id && (
                  <div className="flex flex-wrap items-end gap-2 mt-4" data-testid={`add-template-item-${tpl.id}`}>
                    <div className="min-w-[220px]">
                      <Label className="text-xs">Add amenity item</Label>
                      <Select value={newTemplateItem[tpl.id] || ""} onValueChange={value => setNewTemplateItem(prev => ({ ...prev, [tpl.id]: value }))}>
                        <SelectTrigger className="h-10 mt-1" data-testid={`template-item-select-${tpl.id}`}><SelectValue placeholder="Choose item" /></SelectTrigger>
                        <SelectContent>{items.filter(item => item.active !== false).map(item => <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <Button onClick={() => addTemplateItem(tpl)} className="h-10 bg-slate-900 text-white hover:bg-slate-800" data-testid={`template-add-item-button-${tpl.id}`}>Add Item</Button>
                  </div>
                )}
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="config">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="p-4">
              <div className="section-sub mb-3 font-medium text-slate-700">Departments</div>
              <div className="flex gap-2 mb-3">
                <Input value={newDept} onChange={e => setNewDept(e.target.value)} placeholder="New department" className="h-10" data-testid="new-dept-name" />
                <Button onClick={() => addNamed("departments", newDept, setNewDept)} className="bg-slate-900 text-white" data-testid="add-dept">Add</Button>
              </div>
              {depts.map(d => (
                <div key={d.id} className="flex items-center justify-between border-b border-slate-100 py-1.5 text-sm">
                  <span>{d.name}</span>
                  <Button size="sm" variant="outline" onClick={() => toggleNamed("departments", d.id, !d.active)} data-testid={`toggle-dept-${d.name.replace(/\s/g, "-")}`}>{d.active ? "Deactivate" : "Activate"}</Button>
                </div>
              ))}
            </Card>
            <Card className="p-4">
              <div className="section-sub mb-3 font-medium text-slate-700">Categories</div>
              <div className="flex gap-2 mb-3">
                <Input value={newCat} onChange={e => setNewCat(e.target.value)} placeholder="New category" className="h-10" data-testid="new-cat-name" />
                <Button onClick={() => addNamed("categories", newCat, setNewCat)} className="bg-slate-900 text-white" data-testid="add-cat">Add</Button>
              </div>
              {cats.map(c => (
                <div key={c.id} className="flex items-center justify-between border-b border-slate-100 py-1.5 text-sm">
                  <span>{c.name}</span>
                  <Button size="sm" variant="outline" onClick={() => toggleNamed("categories", c.id, !c.active)}>{c.active ? "Deactivate" : "Activate"}</Button>
                </div>
              ))}
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="prefs">
          <Card className="p-6 space-y-4 max-w-xl">
            <div><Label>Asset ID Prefix</Label>
              <Input value={settings.tag_id_prefix || ""} onChange={e => setSettings({ ...settings, tag_id_prefix: e.target.value.toUpperCase() })} className="h-11 mt-1 w-32" data-testid="tag-prefix-input" />
              <div className="text-xs text-slate-500 mt-1">Applies to future IDs only — issued codes stay unique and immutable.</div>
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={!!settings.grant_laundry_access} onCheckedChange={v => setSettings({ ...settings, grant_laundry_access: v })} data-testid="laundry-access-switch" />
              <div className="text-sm">Grant Laundry role read access to Asset Management</div>
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={!!settings.allow_supervisor_purchase_draft} onCheckedChange={v => setSettings({ ...settings, allow_supervisor_purchase_draft: v })} data-testid="supervisor-po-switch" />
              <div className="text-sm">Allow Housekeeping Supervisor to create purchase drafts (approval stays Admin-only)</div>
            </div>
            <Button className="bg-slate-900 text-white hover:bg-slate-800" data-testid="save-asset-settings"
              onClick={async () => { try { await api.patch("/assets/settings", { tag_id_prefix: settings.tag_id_prefix, grant_laundry_access: settings.grant_laundry_access, allow_supervisor_purchase_draft: settings.allow_supervisor_purchase_draft }); toast.success("Settings saved"); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } }}>Save</Button>
          </Card>

          <Card className="p-6 space-y-3 max-w-xl mt-4" data-testid="uom-master-card">
            <div className="section-sub font-medium text-slate-700">Base Units of Measure (UOM)</div>
            <div className="text-xs text-slate-500">Suggested units are always available. Litre & Kilogram accept up to 3 decimals; all others are whole numbers. Add custom units below and choose their decimal policy.</div>
            <div>
              <div className="text-xs font-medium text-slate-600 mb-1">Suggested</div>
              <div className="flex flex-wrap gap-1.5">
                {["Litre (0.000)","Millilitre","Kilogram (0.000)","Gram","Bottle","Piece","Sachet","Kit","Roll","Box","Pack","Set","Pair","Can","Tube","Bag"].map(u => (
                  <Badge key={u} variant="outline" className="border-slate-300 text-slate-600">{u}</Badge>
                ))}
              </div>
            </div>
            <div>
              <div className="text-xs font-medium text-slate-600 mb-1">Custom units</div>
              {(settings.custom_uoms || []).length === 0 && <div className="text-xs text-slate-400 mb-2">No custom units yet.</div>}
              {(settings.custom_uoms || []).map(u => (
                <div key={u.name} className="flex items-center justify-between border-b border-slate-100 py-1.5 text-sm" data-testid={`uom-row-${u.name}`}>
                  <span>{u.name} <span className="text-xs text-slate-400">({Number(u.decimals) >= 3 ? "up to 3 decimals" : "whole numbers"})</span></span>
                  <Button size="sm" variant="outline" onClick={() => removeUom(u.name)} data-testid={`uom-remove-${u.name}`}>Remove</Button>
                </div>
              ))}
              <div className="flex flex-wrap gap-2 items-end mt-2">
                <div><Label className="text-xs">New unit name</Label><Input value={newUom.name} onChange={e => setNewUom({ ...newUom, name: e.target.value })} placeholder="e.g. Carton" className="h-10 mt-1 w-40" data-testid="new-uom-name" /></div>
                <div><Label className="text-xs">Decimals</Label>
                  <Select value={String(newUom.decimals)} onValueChange={v => setNewUom({ ...newUom, decimals: Number(v) })}>
                    <SelectTrigger className="h-10 mt-1 w-40" data-testid="new-uom-decimals"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="0">Whole numbers</SelectItem><SelectItem value="3">Up to 3 decimals</SelectItem></SelectContent>
                  </Select>
                </div>
                <Button onClick={addUom} className="h-10 bg-slate-900 text-white hover:bg-slate-800" data-testid="add-uom">Add Unit</Button>
              </div>
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </Layout>
  );
}
